import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Modal,
  Row,
  Table,
} from 'antd';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import {
  StockTransferFormValues,
  StockTransferItem,
} from '@/core/domain/stock/Operations/Operations.types';
import StockTransferForm, {
  getRowKey,
} from '@/components/Movements/StockTransfer/StockTransferForm';
import {
  ErrorStockTransfer,
  useStockTransfer,
} from '@/core/domain/stock/Operations/Operations.hooks';
import { useHistory } from 'react-router-dom';
import StatusTag from '@/components/_common/StatusTag';
import { useDisclosure } from '@/hooks';
import { FloatingFormItem } from '@/components/_common';
import { useState } from 'react';

export default function StockTransferCreatePage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [form] = Form.useForm<StockTransferFormValues>();
  const items = Form.useWatch('items', form);

  const [modalOpen, modalAction] = useDisclosure();
  const [updateValues, setUpdateValues] = useState<boolean>();

  const stockTransfer = useStockTransfer();

  const onOk = async () => {
    const values = await form.validateFields();

    try {
      const result = await stockTransfer.mutateAsync({
        companySendStockId: values.companySendStockId,
        companyReceiveStockId: values.companyReceiveStockId,
        updateValues,
        operations: values.items.map((item) => ({
          quantity: item.quantity,
          consumableId: item.consumableId,
          partId: item?.partId,
        })),
      });

      history.push(`/app/movements/stock-transfer/${result.result.id}`);
    } catch (error) {
      const axiosError = error as ErrorStockTransfer;

      handleStockTransferError(axiosError);
    }
  };

  const onCancel = async () => {
    modalAction.close();
  };

  function handleStockTransferError(error: ErrorStockTransfer) {
    const fieldErrors = error.response?.data?.fieldErrors;

    if (!fieldErrors) return;

    const fieldErrorKeys = Object.keys(fieldErrors).reduce<number[]>(
      (acc, key) => {
        const match = key.match(/Operations\[(\d+)\]/);
        if (!match) return acc;

        const index = Number(match[1]);
        const item = items[index];

        if (!item) return acc;

        const id = item.partId ?? item.consumableId;

        if (id && !acc.includes(id)) {
          acc.push(id);
        }

        return acc;
      },
      [],
    );

    const data = fieldErrorKeys.map((key) => {
      return items.find((item) =>
        !!item.partId ? item.partId === key : item.consumableId === key,
      );
    }) as StockTransferItem[];

    return Modal.confirm({
      title: 'Existem itens com inconsistência',
      content: (
        <Table
          columns={[
            {
              width: 120,
              title: 'Tipo',
              render: (_, row) => (
                <StatusTag
                  label={row.partId ? 'Peça' : 'Insumo'}
                  color="blue"
                />
              ),
            },
            {
              dataIndex: 'description',
              title: 'Descrição',
            },
          ]}
          dataSource={data}
          pagination={false}
          rowKey={(row) => getRowKey(row)}
        />
      ),
      cancelButtonProps: {
        style: { display: 'none' },
      },
    });
  }

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        extra={
          <Button block type="primary" onClick={modalAction.open}>
            Transferir
          </Button>
        }
      >
        <Form form={form}>
          <StockTransferForm />
        </Form>

        <Divider />

        <SpaceForButton>
          <ButtonBack path="/app/movements/stock-transfer" />
        </SpaceForButton>

        <Modal
          width={600}
          onOk={onOk}
          onCancel={onCancel}
          open={modalOpen}
          title="Transferência de estoque"
          okText="Transferir"
          destroyOnClose
          confirmLoading={stockTransfer.isLoading}
        >
          <Form>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Alert
                  type="warning"
                  message="Deseja realmente realizar a transferência de estoque?"
                />
              </Col>

              <Col span={24}>
                <FloatingFormItem valuePropName="checked">
                  <Checkbox
                    checked={updateValues}
                    onChange={({ target }) => {
                      setUpdateValues(target.checked);
                    }}
                  >
                    Atualizar informações da peça na empresa de destino
                  </Checkbox>
                </FloatingFormItem>
              </Col>

              {updateValues && (
                <Col span={24}>
                  <Alert
                    type="info"
                    showIcon
                    message="Atenção"
                    description="Ao marcar esta opção, os campos preço de custo, margem de lucro e preço de venda terão seus valores atualizados."
                  />
                </Col>
              )}
            </Row>
          </Form>
        </Modal>
      </TabContentWrapper>
    </>
  );
}
